<template>
  <!-- Billing -->
  <v-card>
    <v-card-title>
      <helpers-section-sub-header>Billing information</helpers-section-sub-header>
    </v-card-title>
    <v-card-text>
      <v-container v-if="userRoot.isSubscriber" fluid pa-0>
        <form ref="subscriptionForm" role="form">
          <input type="hidden" name="recurly-token" data-recurly="token" />

          <!-- Name -->
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="billingInfo.first_name"
                data-recurly="first_name"
                label="First name"
                min="1"
                max="100"
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="billingInfo.last_name"
                data-recurly="last_name"
                label="Last name"
                max="100"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Address -->
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="billingInfo.address1"
                data-recurly="address1"
                label="Address 1"
                max="100"
                name="address1"
                hide-details
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="billingInfo.address2"
                data-recurly="address2"
                label="Address 2"
                max="100"
                name="address2"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Country, State, City, Zip code -->
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="billingInfo.country"
                :items="countries"
                item-title="text"
                label="Country"
                hide-details
              />
              <input
                id="country"
                v-model="billingInfo.country"
                data-recurly="country"
                type="hidden"
                name="country"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-autocomplete
                v-model="billingInfo.state"
                :items="states"
                item-title="text"
                label="State"
                hide-details
              />
              <input
                id="state"
                v-model="billingInfo.state"
                data-recurly="state"
                type="hidden"
                name="state"
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                id="city"
                v-model="billingInfo.city"
                data-recurly="city"
                label="City"
                max="100"
                name="city"
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="billingInfo.zip"
                data-recurly="postal_code"
                mask="############"
                label="Postal Code"
                name="postal_code"
                hide-details
              />
            </v-col>
          </v-row>

          <!-- Credit card: Currently used number -->
          <helpers-section-sub-header class="mt-6"> Credit card </helpers-section-sub-header>
          <div v-if="billingInfo.number_current">
            Currently using <span class="font-weight-medium">{{ billingInfo.number_current }}</span
            >.
          </div>

          <!-- Credit card: General error notification -->
          <v-row v-if="error" my-6>
            <v-col cols="12" class="text-error">
              The credit card number is not valid or accepted or the expiration month/year or card
              CVV is incorrect.
            </v-col>
          </v-row>

          <!-- Credit card: Accepted cards logos -->
          <div class="card-logos-container mt-6">
            <img :src="visaLogo" alt="Pay with Visa" />
            <img :src="mastercardLogo" alt="Pay with Mastercard" />
            <img :src="amexLogo" alt="Pay with American Express" />
            <img :src="discoverLogo" alt="Pay with Discover" />
          </div>

          <!-- Credit card: Input fields -->
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <div id="number" data-recurly="number" />
            </v-col>
            <v-col cols="12" pa-0 />
            <!-- new line -->
            <v-col cols="5" sm="3" md="2">
              Month (MM):
              <div id="month" data-recurly="month" />
            </v-col>
            <v-col cols="5" sm="3" md="2">
              Year (YY):
              <div id="year" data-recurly="year" />
            </v-col>
            <v-col cols="2" sm="2" md="1">
              CVV:
              <div id="cvv" data-recurly="cvv" />
            </v-col>
          </v-row>
        </form>
      </v-container>
      <v-container v-else>
        <a href="https://whalewisdom.com/subscription/subscribe_form">Click Here To Subscribe</a>
      </v-container>
      <v-dialog v-model="loading" persistent width="300">
        <v-card color="primary" dark class="pa-2">
          <v-card-text>
            Loading subscription information....
            <v-progress-linear indeterminate color="accent" class="mb-0" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card-text>

    <v-divider />
    <v-card-actions class="pa-4">
      <v-btn
        v-if="userRoot.isSubscriber"
        :loading="savingBillingInfo"
        :disabled="savingBillingInfo"
        color="primary"
        @click="saveBillingInfo()"
      >
        Save
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapStoreState, mapStoreGetters } from '@/store/helpers'

// Images
import visaLogo from '@/assets/images/credit_card_logos/visa.png'
import mastercardLogo from '@/assets/images/credit_card_logos/mastercard.png'
import amexLogo from '@/assets/images/credit_card_logos/americanexpress.png'
import discoverLogo from '@/assets/images/credit_card_logos/discover.png'

// Mixins
import FormattersMixin from '@/mixins/formatters'

export default {
  mixins: [FormattersMixin],

  data() {
    return {
      visaLogo,
      mastercardLogo,
      amexLogo,
      discoverLogo,
      savingBillingInfo: false,
      billingInfo: {},
      loading: false,
      error: false,
      countries: [
        { value: 'AF', text: 'Afghanistan' },
        { value: 'AX', text: 'Åland Islands' },
        { value: 'AL', text: 'Albania' },
        { value: 'DZ', text: 'Algeria' },
        { value: 'AS', text: 'American Samoa' },
        { value: 'AD', text: 'Andorra' },
        { value: 'AO', text: 'Angola' },
        { value: 'AI', text: 'Anguilla' },
        { value: 'AQ', text: 'Antarctica' },
        { value: 'AG', text: 'Antigua and Barbuda' },
        { value: 'AR', text: 'Argentina' },
        { value: 'AM', text: 'Armenia' },
        { value: 'AW', text: 'Aruba' },
        { value: 'AU', text: 'Australia' },
        { value: 'AT', text: 'Austria' },
        { value: 'AZ', text: 'Azerbaijan' },
        { value: 'BS', text: 'Bahamas' },
        { value: 'BH', text: 'Bahrain' },
        { value: 'BD', text: 'Bangladesh' },
        { value: 'BB', text: 'Barbados' },
        { value: 'BY', text: 'Belarus' },
        { value: 'BE', text: 'Belgium' },
        { value: 'BZ', text: 'Belize' },
        { value: 'BJ', text: 'Benin' },
        { value: 'BM', text: 'Bermuda' },
        { value: 'BT', text: 'Bhutan' },
        { value: 'BO', text: 'Bolivia, Plurinational State of' },
        { value: 'BA', text: 'Bosnia and Herzegovina' },
        { value: 'BW', text: 'Botswana' },
        { value: 'BV', text: 'Bouvet Island' },
        { value: 'BR', text: 'Brazil' },
        { value: 'IO', text: 'British Indian Ocean Territory' },
        { value: 'BN', text: 'Brunei Darussalam' },
        { value: 'BG', text: 'Bulgaria' },
        { value: 'BF', text: 'Burkina Faso' },
        { value: 'BI', text: 'Burundi' },
        { value: 'KH', text: 'Cambodia' },
        { value: 'CM', text: 'Cameroon' },
        { value: 'CA', text: 'Canada' },
        { value: 'CV', text: 'Cape Verde' },
        { value: 'KY', text: 'Cayman Islands' },
        { value: 'CF', text: 'Central African Republic' },
        { value: 'TD', text: 'Chad' },
        { value: 'CL', text: 'Chile' },
        { value: 'CN', text: 'China' },
        { value: 'CX', text: 'Christmas Island' },
        { value: 'CC', text: 'Cocos (Keeling) Islands' },
        { value: 'CO', text: 'Colombia' },
        { value: 'KM', text: 'Comoros' },
        { value: 'CG', text: 'Congo' },
        { value: 'CD', text: 'Congo, the Democratic Republic of the' },
        { value: 'CK', text: 'Cook Islands' },
        { value: 'CR', text: 'Costa Rica' },
        { value: 'CI', text: 'CÔte D&#x27;ivoire' },
        { value: 'HR', text: 'Croatia' },
        { value: 'CU', text: 'Cuba' },
        { value: 'CY', text: 'Cyprus' },
        { value: 'CZ', text: 'Czech Republic' },
        { value: 'DK', text: 'Denmark' },
        { value: 'DJ', text: 'Djibouti' },
        { value: 'DM', text: 'Dominica' },
        { value: 'DO', text: 'Dominican Republic' },
        { value: 'EC', text: 'Ecuador' },
        { value: 'EG', text: 'Egypt' },
        { value: 'SV', text: 'El Salvador' },
        { value: 'GQ', text: 'Equatorial Guinea' },
        { value: 'ER', text: 'Eritrea' },
        { value: 'EE', text: 'Estonia' },
        { value: 'ET', text: 'Ethiopia' },
        { value: 'FK', text: 'Falkland Islands (Malvinas)' },
        { value: 'FO', text: 'Faroe Islands' },
        { value: 'FJ', text: 'Fiji' },
        { value: 'FI', text: 'Finland' },
        { value: 'FR', text: 'France' },
        { value: 'GF', text: 'French Guiana' },
        { value: 'PF', text: 'French Polynesia' },
        { value: 'TF', text: 'French Southern Territories' },
        { value: 'GA', text: 'Gabon' },
        { value: 'GM', text: 'Gambia' },
        { value: 'GE', text: 'Georgia' },
        { value: 'DE', text: 'Germany' },
        { value: 'GH', text: 'Ghana' },
        { value: 'GI', text: 'Gibraltar' },
        { value: 'GR', text: 'Greece' },
        { value: 'GL', text: 'Greenland' },
        { value: 'GD', text: 'Grenada' },
        { value: 'GP', text: 'Guadeloupe' },
        { value: 'GU', text: 'Guam' },
        { value: 'GT', text: 'Guatemala' },
        { value: 'GG', text: 'Guernsey' },
        { value: 'GN', text: 'Guinea' },
        { value: 'GW', text: 'Guinea-bissau' },
        { value: 'GY', text: 'Guyana' },
        { value: 'HT', text: 'Haiti' },
        { value: 'HM', text: 'Heard Island and Mcdonald Islands' },
        { value: 'VA', text: 'Vatican City State' },
        { value: 'HN', text: 'Honduras' },
        { value: 'HK', text: 'Hong Kong' },
        { value: 'HU', text: 'Hungary' },
        { value: 'IS', text: 'Iceland' },
        { value: 'IN', text: 'India' },
        { value: 'ID', text: 'Indonesia' },
        { value: 'IR', text: 'Iran, Islamic Republic of' },
        { value: 'IQ', text: 'Iraq' },
        { value: 'IE', text: 'Ireland' },
        { value: 'IM', text: 'Isle of Man' },
        { value: 'IL', text: 'Israel' },
        { value: 'IT', text: 'Italy' },
        { value: 'JM', text: 'Jamaica' },
        { value: 'JP', text: 'Japan' },
        { value: 'JE', text: 'Jersey' },
        { value: 'JO', text: 'Jordan' },
        { value: 'KZ', text: 'Kazakhstan' },
        { value: 'KE', text: 'Kenya' },
        { value: 'KI', text: 'Kiribati' },
        { value: 'KP', text: 'Korea, Democratic People&#x27;s Republic of' },
        { value: 'KR', text: 'Korea, Republic of' },
        { value: 'KW', text: 'Kuwait' },
        { value: 'KG', text: 'Kyrgyzstan' },
        { value: 'LA', text: 'Lao People&#x27;s Democratic Republic' },
        { value: 'LV', text: 'Latvia' },
        { value: 'LB', text: 'Lebanon' },
        { value: 'LS', text: 'Lesotho' },
        { value: 'LR', text: 'Liberia' },
        { value: 'LY', text: 'Libyan Arab Jamahiriya' },
        { value: 'LI', text: 'Liechtenstein' },
        { value: 'LT', text: 'Lithuania' },
        { value: 'LU', text: 'Luxembourg' },
        { value: 'MO', text: 'Macao' },
        { value: 'MK', text: 'Macedonia, the Former Yugoslav Republic of' },
        { value: 'MG', text: 'Madagascar' },
        { value: 'MW', text: 'Malawi' },
        { value: 'MY', text: 'Malaysia' },
        { value: 'MV', text: 'Maldives' },
        { value: 'ML', text: 'Mali' },
        { value: 'MT', text: 'Malta' },
        { value: 'MH', text: 'Marshall Islands' },
        { value: 'MQ', text: 'Martinique' },
        { value: 'MR', text: 'Mauritania' },
        { value: 'MU', text: 'Mauritius' },
        { value: 'YT', text: 'Mayotte' },
        { value: 'MX', text: 'Mexico' },
        { value: 'FM', text: 'Micronesia, Federated States of' },
        { value: 'MD', text: 'Moldova, Republic of' },
        { value: 'MC', text: 'Monaco' },
        { value: 'MN', text: 'Mongolia' },
        { value: 'ME', text: 'Montenegro' },
        { value: 'MS', text: 'Montserrat' },
        { value: 'MA', text: 'Morocco' },
        { value: 'MZ', text: 'Mozambique' },
        { value: 'MM', text: 'Myanmar' },
        { value: 'NA', text: 'Namibia' },
        { value: 'NR', text: 'Nauru' },
        { value: 'NP', text: 'Nepal' },
        { value: 'NL', text: 'Netherlands' },
        { value: 'AN', text: 'Netherlands Antilles' },
        { value: 'NC', text: 'New Caledonia' },
        { value: 'NZ', text: 'New Zealand' },
        { value: 'NI', text: 'Nicaragua' },
        { value: 'NE', text: 'Niger' },
        { value: 'NG', text: 'Nigeria' },
        { value: 'NU', text: 'Niue' },
        { value: 'NF', text: 'Norfolk Island' },
        { value: 'MP', text: 'Northern Mariana Islands' },
        { value: 'NO', text: 'Norway' },
        { value: 'OM', text: 'Oman' },
        { value: 'PK', text: 'Pakistan' },
        { value: 'PW', text: 'Palau' },
        { value: 'PS', text: 'Palestinian Territory, Occupied' },
        { value: 'PA', text: 'Panama' },
        { value: 'PG', text: 'Papua New Guinea' },
        { value: 'PY', text: 'Paraguay' },
        { value: 'PE', text: 'Peru' },
        { value: 'PH', text: 'Philippines' },
        { value: 'PN', text: 'Pitcairn' },
        { value: 'PL', text: 'Poland' },
        { value: 'PT', text: 'Portugal' },
        { value: 'PR', text: 'Puerto Rico' },
        { value: 'QA', text: 'Qatar' },
        { value: 'RE', text: 'RÉunion' },
        { value: 'RO', text: 'Romania' },
        { value: 'RU', text: 'Russian Federation' },
        { value: 'RW', text: 'Rwanda' },
        { value: 'BL', text: 'Saint BarthÉlemy' },
        { value: 'SH', text: 'Saint Helena, Ascension and Tristan Da Cunha' },
        { value: 'KN', text: 'Saint Kitts and Nevis' },
        { value: 'LC', text: 'Saint Lucia' },
        { value: 'MF', text: 'Saint Martin' },
        { value: 'PM', text: 'Saint Pierre and Miquelon' },
        { value: 'VC', text: 'Saint Vincent and the Grenadines' },
        { value: 'WS', text: 'Samoa' },
        { value: 'SM', text: 'San Marino' },
        { value: 'ST', text: 'Sao Tome and Principe' },
        { value: 'SA', text: 'Saudi Arabia' },
        { value: 'SN', text: 'Senegal' },
        { value: 'RS', text: 'Serbia' },
        { value: 'SC', text: 'Seychelles' },
        { value: 'SL', text: 'Sierra Leone' },
        { value: 'SG', text: 'Singapore' },
        { value: 'SK', text: 'Slovakia' },
        { value: 'SI', text: 'Slovenia' },
        { value: 'SB', text: 'Solomon Islands' },
        { value: 'SO', text: 'Somalia' },
        { value: 'ZA', text: 'South Africa' },
        { value: 'GS', text: 'South Georgia and the South Sandwich Islands' },
        { value: 'ES', text: 'Spain' },
        { value: 'LK', text: 'Sri Lanka' },
        { value: 'SD', text: 'Sudan' },
        { value: 'SR', text: 'Suriname' },
        { value: 'SJ', text: 'Svalbard and Jan Mayen' },
        { value: 'SZ', text: 'Swaziland' },
        { value: 'SE', text: 'Sweden' },
        { value: 'CH', text: 'Switzerland' },
        { value: 'SY', text: 'Syrian Arab Republic' },
        { value: 'TW', text: 'Taiwan, Province of China' },
        { value: 'TJ', text: 'Tajikistan' },
        { value: 'TZ', text: 'Tanzania, United Republic of' },
        { value: 'TH', text: 'Thailand' },
        { value: 'TL', text: 'Timor-leste' },
        { value: 'TG', text: 'Togo' },
        { value: 'TK', text: 'Tokelau' },
        { value: 'TO', text: 'Tonga' },
        { value: 'TT', text: 'Trinidad and Tobago' },
        { value: 'TN', text: 'Tunisia' },
        { value: 'TR', text: 'Turkey' },
        { value: 'TM', text: 'Turkmenistan' },
        { value: 'TC', text: 'Turks and Caicos Islands' },
        { value: 'TV', text: 'Tuvalu' },
        { value: 'UG', text: 'Uganda' },
        { value: 'UA', text: 'Ukraine' },
        { value: 'AE', text: 'United Arab Emirates' },
        { value: 'GB', text: 'United Kingdom' },
        { value: 'US', text: 'United States' },
        { value: 'UM', text: 'United States Minor Outlying Islands' },
        { value: 'UY', text: 'Uruguay' },
        { value: 'UZ', text: 'Uzbekistan' },
        { value: 'VU', text: 'Vanuatu' },
        { value: 'VE', text: 'Venezuela, Bolivarian Republic of' },
        { value: 'VN', text: 'Viet Nam' },
        { value: 'VG', text: 'Virgin Islands, British' },
        { value: 'VI', text: 'Virgin Islands, U.S.' },
        { value: 'WF', text: 'Wallis and Futuna' },
        { value: 'EH', text: 'Western Sahara' },
        { value: 'YE', text: 'Yemen' },
        { value: 'ZM', text: 'Zambia' },
        { value: 'ZW', text: 'Zimbabwe' }
      ],
      states: [
        { value: 'AL', text: 'Alabama' },
        { value: 'AK', text: 'Alaska' },
        { value: 'AZ', text: 'Arizona' },
        { value: 'AR', text: 'Arkansas' },
        { value: 'CA', text: 'California' },
        { value: 'CO', text: 'Colorado' },
        { value: 'CT', text: 'Connecticut' },
        { value: 'DC', text: 'District of Columbia' },
        { value: 'DE', text: 'Delaware' },
        { value: 'FL', text: 'Florida' },
        { value: 'GA', text: 'Georgia' },
        { value: 'HI', text: 'Hawaii' },
        { value: 'ID', text: 'Idaho' },
        { value: 'IL', text: 'Illinois' },
        { value: 'IN', text: 'Indiana' },
        { value: 'IA', text: 'Iowa' },
        { value: 'KS', text: 'Kansas' },
        { value: 'KY', text: 'Kentucky' },
        { value: 'LA', text: 'Louisiana' },
        { value: 'ME', text: 'Maine' },
        { value: 'MD', text: 'Maryland' },
        { value: 'MA', text: 'Massachusetts' },
        { value: 'MI', text: 'Michigan' },
        { value: 'MN', text: 'Minnesota' },
        { value: 'MS', text: 'Mississippi' },
        { value: 'MO', text: 'Missouri' },
        { value: 'MT', text: 'Montana' },
        { value: 'NE', text: 'Nebraska' },
        { value: 'NV', text: 'Nevada' },
        { value: 'NH', text: 'New Hampshire' },
        { value: 'NJ', text: 'New Jersey' },
        { value: 'NM', text: 'New Mexico' },
        { value: 'NY', text: 'New York' },
        { value: 'NC', text: 'North Carolina' },
        { value: 'ND', text: 'North Dakota' },
        { value: 'OH', text: 'Ohio' },
        { value: 'OK', text: 'Oklahoma' },
        { value: 'OR', text: 'Oregon' },
        { value: 'PA', text: 'Pennsylvania' },
        { value: 'RI', text: 'Rhode Island' },
        { value: 'SC', text: 'South Carolina' },
        { value: 'SD', text: 'South Dakota' },
        { value: 'TN', text: 'Tennessee' },
        { value: 'TX', text: 'Texas' },
        { value: 'UT', text: 'Utah' },
        { value: 'VT', text: 'Vermont' },
        { value: 'VA', text: 'Virginia' },
        { value: 'WA', text: 'Washington' },
        { value: 'WV', text: 'West Virginia' },
        { value: 'WI', text: 'Wisconsin' },
        { value: 'WY', text: 'Wyoming' }
      ]
    }
  },

  computed: {
    ...mapStoreState({
      userRoot: state => state.user,
      user: state => state.user.user
    }),
    ...mapStoreGetters('user', ['subscription'])
  },

  mounted() {
    this.loadRecurlyScript()
    this.updateBillingInfo()
  },

  methods: {
    saveBillingInfo() {
      this.savingBillingInfo = true

      const billingInfo = {
        number: this.billingInfo.number,
        month: this.billingInfo.month,
        year: this.billingInfo.year,
        cvv: this.billingInfo.cvv,
        first_name: this.billingInfo.first_name,
        last_name: this.billingInfo.last_name,
        address1: this.billingInfo.address1,
        address2: this.billingInfo.address2,
        currency: 'USD',
        email: this.user.email,
        city: this.billingInfo.city,
        state: this.billingInfo.state,
        postal_code: this.billingInfo.zip,
        country: this.billingInfo.country || 'US'
      }

      this.getRecurlyToken(billingInfo, this.finalizeBillingUpdate)
    },

    getRecurlyToken(billingInfo, callback) {
      if (typeof window.recurly === 'undefined') {
        throw new Error('Recurly API Library Missing.')
      }

      this.error = false
      const vm = this

      window.recurly.token(vm.$refs.subscriptionForm, function (error, token) {
        if (error) {
          useAppStore().showNotification({ type: 'error', text: error.message })
          vm.error = true
          vm.savingBillingInfo = false
        } else {
          callback(token)
        }
      })
    },

    finalizeBillingUpdate(token) {
      api
        .post('user.json', {
          account_action: 'update_billing',
          recurly_token: token.id
        })
        .then(response => {
          if (response.data) {
            this.updateBillingInfo()
            useAppStore().showNotification({
              type: 'success',
              text: 'Your billing information has been updated'
            })
          }
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: error.response.data || 'Update failed: Please verify your billing information'
          })
        })
        .then(() => (this.savingBillingInfo = false))
    },

    loadRecurlyScript() {
      const vm = this
      const recurlyScript = document.createElement('script')

      recurlyScript.src = 'https://js.recurly.com/v4/recurly.js'
      recurlyScript.onload = function () {
        vm.$nextTick(function () {
          window.recurly.configure({
            publicKey: 'sc-VLk3iuMQaQDqtzjMo6iLMw',
            fields: {
              all: {
                style: {
                  padding: '8px'
                }
              },
              number: {
                style: {
                  placeholder: {
                    content: 'Card number'
                  }
                }
              },
              month: {
                style: {
                  placeholder: {
                    content: 'MM'
                  }
                }
              },
              year: {
                style: {
                  placeholder: {
                    content: 'YY'
                  }
                }
              },
              cvv: {
                style: {
                  placeholder: {
                    content: 'CVV'
                  }
                }
              }
            }
          })
        })
      }

      document.head.appendChild(recurlyScript)
    },

    updateBillingInfo() {
      if (this.userRoot.isSubscriber && this.user.recurly_subscriber) {
        this.loading = true
        api
          .get('billing_info')
          .then(response => {
            if (response.data) {
              this.billingInfo = response.data.billing_info
              this.currentPlanCode = response.data.plan_code

              if (this.billingInfo.billing_type === 'credit_card') {
                // eslint-disable-next-line
                this.billingInfo.number_current = `${this.billingInfo.card_type} ‪•⁠ ⁠•⁠ ⁠•⁠ ⁠• ${this.billingInfo.last_four}`
              }
            }
            this.loading = false
          })
          .catch(error => {
            useAppStore().showNotification({
              type: 'error',
              text: (error.response && error.response.data) || error.message
            })
            this.loading = false
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card-logos-container {
  img {
    max-width: 50px;
    margin-right: 4px;
  }
}

.recurly-hosted-field {
  border: 1px solid rgba($base-font, 0.1);
  height: 40px;
}
</style>
<style>
.recurly-hosted-field {
  background-color: rgba(0, 0, 0, 0);

  border-bottom-color: rgba(0, 0, 0, 0.87);
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
  border-image-outset: 0px;
  border-image-repeat: stretch;
  border-image-slice: 100%;
  border-image-source: none;
  border-image-width: 1;
  border-left-color: rgba(0, 0, 0, 0.87);
  border-left-style: none;
  border-left-width: 0px;
  border-right-color: rgba(0, 0, 0, 0.87);
  border-right-style: none;
  border-right-width: 0px;
  border-top-color: rgba(0, 0, 0, 0.87);
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  border-top-style: none;
  border-top-width: 0px;
  box-shadow: none;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  box-sizing: border-box;
  caret-color: rgb(25, 118, 210);
  color: rgba(0, 0, 0, 0.87);
  cursor: auto;
  display: block;
  flex-basis: 0%;
  flex-grow: 1;
  flex-shrink: 1;
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  font-variant-caps: normal;
  font-variant-ligatures: normal;
  font-variant-numeric: normal;
  font-weight: normal;
  height: 30px;
  letter-spacing: normal;
  line-height: 24px;
  margin-bottom: 10px;
  margin-left: 0px;
  margin-right: 0px;
  margin-top: 0px;
  min-width: 0px;
  overflow-x: hidden;
  overflow-y: hidden;
  padding-bottom: 0px;
  padding-left: 0px;
  padding-right: 0px;
  padding-top: 0px;
  text-align: start;
  text-indent: 0px;
  text-overflow: ellipsis;
  text-rendering: auto;
  text-shadow: none;
  text-size-adjust: 100%;
  text-transform: none;
  user-select: text;
  white-space: nowrap;
  width: 373.656px;
  word-spacing: 0px;
  writing-mode: horizontal-tb;
  -webkit-appearance: none;
  -webkit-box-direction: normal;
  -webkit-box-flex: 1;
  -webkit-font-smoothing: antialiased;
  -webkit-rtl-ordering: logical;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-border-image: none;
}

.recurly-hosted-field-focus {
}

.recurly-hosted-field-number {
}

.recurly-hosted-field-month {
  max-width: 80px;
}

.recurly-hosted-field-year {
  max-width: 100px;
}

.recurly-hosted-field-cvv {
  max-width: 100px;
}
</style>
